import type { ProgramRow } from '@@/api/program';
import type { OutcomeFilter, ProgramAnalyticsManager } from '@/state/program/useProgramAnalytics';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/utils';
import { SearchInput } from '@/components/SearchInput';
import { formatDate, formatNumber } from 'helpers/formatters';
import { OUTCOME_LABEL, OUTCOME_ORDER, OUTCOME_TEXT, formatDays, formatLift } from './outcome-tones';
import { EmptyState, GRID_TABLE_ROW, GRID_TABLE_ROWGROUP, GridTable, Pager, Panel } from '../ConsolePanel';
import { MICRO_LABEL_SHAPE, PillTabs, SEARCH_FIELD_CLASS, StackedRow } from '../ConsolePanel';

export type ConcludedTableProps = { manager: ProgramAnalyticsManager };

const DAY_MS = 24 * 60 * 60 * 1000;
const TEMPLATE = 'minmax(220px,1fr) 112px 88px 72px 88px 96px';

const OUTCOME_OPTIONS = [
  { label: 'All', value: 'all' },
  ...OUTCOME_ORDER.map((outcome) => ({ label: OUTCOME_LABEL[outcome], value: outcome })),
];

type HeadProps = { children: string; className?: string };

const Head = ({ children, className }: HeadProps) => (
  <div role="columnheader" className={cn('micro-label', className)}>
    {children}
  </div>
);

type OutcomeLabelProps = { row: ProgramRow };

/** "Won · deployed" when the win comes from the rollout rather than the statistics. */
const OutcomeLabel = ({ row }: OutcomeLabelProps) => {
  const outcome = row.reading?.outcome ?? 'no-data';
  return (
    <span className={cn(MICRO_LABEL_SHAPE, OUTCOME_TEXT[outcome])}>
      {OUTCOME_LABEL[outcome]}
      {row.reading?.byDeployment ? ' · deployed' : ''}
    </span>
  );
};

const duration = (row: ProgramRow): number | null =>
  row.concludedAt === null ? null : (row.concludedAt - row.startedAt) / DAY_MS;

/** Every concluded test in the window, newest first; each row opens the experiment. */
export const ConcludedTable = ({ manager }: ConcludedTableProps) => {
  const navigate = useNavigate();
  const { outcome, setOutcome, search, setSearch, pageRows, listedCount, currentPage, totalPages, setPage } = manager;

  const open = (row: ProgramRow) => navigate(`/experiments/${row.experimentId}`);

  return (
    <Panel
      title="Concluded tests"
      tourAnchor="program-concluded"
      description={`${listedCount} in this window. Outcome reads conversions across all currencies, Holm-Bonferroni corrected.`}
      bodyClassName="p-0"
      actions={
        <div className="relative w-full md:w-auto">
          <SearchInput
            className={SEARCH_FIELD_CLASS}
            handleSearch={setSearch}
            value={search}
            placeholder="Search tests..."
          />
        </div>
      }
    >
      <div className="border-line border-b px-3.5 py-2.5" data-tour="program-outcomes">
        <PillTabs options={OUTCOME_OPTIONS} value={outcome} onChange={(next) => setOutcome(next as OutcomeFilter)} />
      </div>

      {!pageRows.length ? (
        <div className="p-3.5">
          <EmptyState>No concluded test matches.</EmptyState>
        </div>
      ) : (
        <>
          <div className="grid gap-2 p-3.5 md:hidden">
            {pageRows.map((row) => (
              <button key={row.experimentId} type="button" className="text-left" onClick={() => open(row)}>
                <StackedRow
                  heading={
                    <div className="flex min-w-0 flex-col">
                      <span className="text-foreground truncate text-body">{row.name}</span>
                      <span className="supporting truncate">{row.websiteUrl}</span>
                    </div>
                  }
                  fields={[
                    { key: 'outcome', label: 'Outcome', value: <OutcomeLabel row={row} /> },
                    { key: 'lift', label: 'Lift', value: formatLift(row.reading?.lift ?? null) },
                    { key: 'length', label: 'Length', value: formatDays(duration(row)) },
                    { key: 'sessions', label: 'Sessions', value: formatNumber(row.sessions) },
                    { key: 'concluded', label: 'Concluded', value: formatDate(row.concludedAt ?? 0) },
                  ]}
                />
              </button>
            ))}
          </div>

          <GridTable label="Concluded tests" minWidth={760} template={TEMPLATE}>
            <div role="row" className={cn(GRID_TABLE_ROW, 'border-line items-center border-b py-2')}>
              <Head>Experiment</Head>
              <Head>Outcome</Head>
              <Head className="text-right">Lift</Head>
              <Head className="text-right">Length</Head>
              <Head className="text-right">Sessions</Head>
              <Head className="text-right">Concluded</Head>
            </div>
            <div role="rowgroup" className={GRID_TABLE_ROWGROUP}>
              {pageRows.map((row) => (
                <div
                  key={row.experimentId}
                  role="row"
                  tabIndex={0}
                  onClick={() => open(row)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') open(row);
                  }}
                  className={cn(
                    GRID_TABLE_ROW,
                    'border-line hover:bg-hover cursor-pointer items-center border-b py-2 last:border-b-0',
                  )}
                >
                  <div role="cell" className="flex min-w-0 flex-col">
                    <span className="text-foreground truncate text-body">{row.name}</span>
                    <span className="supporting truncate">
                      {row.websiteUrl}
                      {row.reading?.variantName ? ` · ${row.reading.variantName}` : ''}
                    </span>
                  </div>
                  <div role="cell">
                    <OutcomeLabel row={row} />
                  </div>
                  <div
                    role="cell"
                    className={cn('num text-right', row.reading?.lift != null ? 'text-foreground' : 'text-dim')}
                  >
                    {formatLift(row.reading?.lift ?? null)}
                  </div>
                  <div role="cell" className="num text-foreground text-right">
                    {formatDays(duration(row))}
                  </div>
                  <div role="cell" className="num text-foreground text-right">
                    {formatNumber(row.sessions)}
                  </div>
                  <div role="cell" className="num text-dim text-right">
                    {formatDate(row.concludedAt ?? 0)}
                  </div>
                </div>
              ))}
            </div>
          </GridTable>
        </>
      )}

      {totalPages > 1 && <Pager currentPage={currentPage} totalPages={totalPages} onChange={setPage} />}
    </Panel>
  );
};
